import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, useTheme, Divider } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { PurchaseOrderItemStatus, PurchaseOrderItem, POItemGroups } from '@domain/purchasing';
import { GroupingOption, KanbanColumn as KanbanColumnType } from '../../../types/types';
import DraggableOrderCard from './DraggableOrderCard';

import { getUngroupedItems, groupBy } from '@features/purchasing/utils/groupItems';

interface KanbanColumnProps {
  column: KanbanColumnType;
  items: PurchaseOrderItem[];
  group: GroupingOption;
  activeId: string | null;
  onStatusChange?: (item: PurchaseOrderItem, newStatus: PurchaseOrderItemStatus) => void;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  group,
  items,
  activeId,
  onStatusChange
}) => {
  const theme = useTheme();

  const [groupedItems, setGroupedItems] = useState<POItemGroups[]>([]);
  const [ungroupedItems, setUngroupedItems] = useState<PurchaseOrderItem[]>([]);


  useEffect(() => {
    setGroupedItems(groupBy(items, group));
    setUngroupedItems(getUngroupedItems(items, group));
  }, [items, group]);

  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const placeholder = (
    <Divider sx={{ height: 2, backgroundColor: 'primary.main' }} />
  )

  return (
    <Box

      ref={setNodeRef}
      sx={{
        borderRadius: 1,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: '300px',
        backgroundColor: isOver ? `${column.color}20` : `${column.color}10`,
        border: isOver ? `1px dashed ${column.color}` : `1px solid ${column.color}20`,
        transition: 'all 0.2s ease'
      }}
    >

      {/* Column Header */}
      <Box
        sx={{
          p: 1,
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1} gap={1}>

          <Stack>
            <Typography variant="subtitle1" >
              {column.title}
            </Typography>
            {column.subtitle && (
              <Typography variant="caption" color="text.secondary">
                {column.subtitle}
              </Typography>
            )}

          </Stack>
        </Stack>
      </Box>

      {/* Column Content */}
      <Box
        sx={{
          flex: 1,
          p: 0.5,
          gap: 0.5,
          overflowY: 'auto',
        }}
      >
        <Stack gap={0.5}>
          {ungroupedItems.map((item) => (

            item.id == activeId ? (
              placeholder
            ) : (
              <DraggableOrderCard
                key={`${item.id}`}
                orderItem={item as PurchaseOrderItem}
                onStatusChange={onStatusChange}
              />
            )
          ))}
        </Stack>

        {groupedItems.map((group) => (
          <Box
            key={group.id}
            sx={{
              background: theme.palette.action.selected,
              borderRadius: 1,
              p: 0.5,
              mb: 0.5
            }}
          >


            <Box
              sx={{
                m: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Typography variant="caption" noWrap>{group.name}</Typography>
            </Box>


            {/* Order Items */}
            <Stack gap={0.5}>
              {group.items.map((item) => (

                item.id == activeId ? (
                  placeholder
                ) : (

                  < DraggableOrderCard
                    key={`${item.id}`}
                    orderItem={item as PurchaseOrderItem}
                    onStatusChange={onStatusChange}
                  />
                )
              ))}
            </Stack>

          </Box>

        ))}
      </Box>



    </Box>
  );
};

export default KanbanColumn; 